import {
  DATE_RANGE_VALUE_REGEX,
  isPeriodGranularity,
  isPresetAvailable,
  parsePeriodValue,
  PERIOD_VALUE_REGEX,
  resolvePeriodRange,
  type PeriodGranularityType
} from '#domain/core/filter/period';
import { TreeSelectionZodSchema } from '#domain/core/filterDefinition/types/treeSelection.type';
import { GRANULARITY_FIELD, PERIOD_FIELD } from '#stores/utils/filterMapping/constants/filterMapping.constant';
import { FilterDefinitionFormType, FilterDefinitionRuleLabel } from '@carrefour-gcs/bff-sdk';
import type { Dayjs } from 'dayjs';
import { z } from 'zod';
import {
  INVALID_DATE_FORMAT_MESSAGE,
  REQUIRED_MESSAGE,
  REQUIRED_MESSAGE_BY_FIELD,
  RESTRICTED_VALUE_MESSAGE,
  SELECTION_REQUIRED_MESSAGE
} from '../constants/dynamicFormSchema.constant';
import { type FieldDefinitionType } from '../types/field.type';
import type { SchemaContextType } from '../types/schemaContext.type';
import { resolveFilterValueRestriction } from './filterRestriction.utils';

/**
 * Wording of the "missing value" message of a field: its own when it has one, the generic one otherwise.
 */
function resolveRequiredMessage(field: FieldDefinitionType): string {
  return REQUIRED_MESSAGE_BY_FIELD[field.fieldName] ?? REQUIRED_MESSAGE;
}

/**
 * Generates the base Zod validator structure depending on the structural type of UI element.
 * @param field - The field definition containing the element type and available selectable choices.
 * @returns A generic base Zod schema type (e.g., ZodString, ZodArray, or ZodEnum).
 * * @example
 * // Input: { element: 'DATE_RANGE' }
 * // Output: z.string().regex(DATE_RANGE_VALUE_REGEX)
 * * @example
 * // Input: { element: 'CHECKBOX' }
 * // Output: z.array(z.union([z.string(), z.number()]))
 * * @example
 * // Input: { element: 'RADIO', options: [{ value: 'HT' }, { value: 'TTC' }] }
 * // Output: z.enum(['HT', 'TTC'])
 * * @example
 * // Input: { element: 'CHECKBOX_TREE' }
 * // Output: z.array(TreeSelectionZodSchema)
 */
function baseSchema(field: FieldDefinitionType): z.ZodType {
  switch (field.element) {
    // The analysis period accepts preset keys and explicit ranges; any other date field (comparison
    // period) only accepts an explicit range. The `error` option covers the missing value case: a
    // dependent field is reset to `undefined` when its parent changes, and Zod would otherwise raise
    // its own untranslated type error.
    case FilterDefinitionFormType.DATE:
    case FilterDefinitionFormType.DATE_RANGE:
      return z
        .string({ error: resolveRequiredMessage(field) })
        .regex(
          field.fieldName === PERIOD_FIELD ? PERIOD_VALUE_REGEX : DATE_RANGE_VALUE_REGEX,
          INVALID_DATE_FORMAT_MESSAGE
        );

    case FilterDefinitionFormType.CHECKBOX:
    case FilterDefinitionFormType.CHECKBOX_GRID:
    case FilterDefinitionFormType.CHECKBOX_CARD:
    case FilterDefinitionFormType.CHECKBOX_GROUP:
      return z.array(z.union([z.string(), z.number()]));

    // A missing value and a value outside the options both mean the same thing to the user:
    // nothing is selected, so they share the required wording rather than the raw Zod one.
    case FilterDefinitionFormType.SELECT:
    case FilterDefinitionFormType.RADIO:
      if (field.options?.length) {
        const values = field.options.map((o) => o.value) as [string, ...string[]];
        return z.enum(values, { error: resolveRequiredMessage(field) });
      }
      return z.string({ error: resolveRequiredMessage(field) });

    case FilterDefinitionFormType.CHECKBOX_TREE:
      return z.array(TreeSelectionZodSchema);

    default:
      return z.string();
  }
}

/**
 * Loops through a collection of validation rules sent by the server and sequentially
 * upgrades the base validator with constraints.
 * @param validator - The pristine, base Zod validator built by baseSchema.
 * @param field - The field definition, holding both its raw validation rules (e.g., REQUIRED, MIN, MAX) and its name.
 * @returns The enhanced Zod schema loaded with all required constraint rules.
 * * @example
 * // Input: baseValidator = z.string(), field.validationRules = [{ label: 'REQUIRED', value: 'true' }]
 * // Output: z.string().min(1, 'filters.form.error.required')
 */
function applyRules(validator: z.ZodTypeAny, field: FieldDefinitionType): z.ZodTypeAny {
  let schema = validator;
  for (const ruleStr of field.validationRules) {
    const { label: rule, value } = ruleStr;
    const numValue = value ? Number.parseInt(value, 10) : null;

    switch (rule) {
      case FilterDefinitionRuleLabel.REQUIRED:
        schema = applyRequiredRule(schema, field);
        break;

      case FilterDefinitionRuleLabel.MIN:
        schema = applyMinRule(numValue, schema);
        break;

      case FilterDefinitionRuleLabel.MAX:
        schema = applyMaxRule(numValue, schema);
        break;

      // Cross-field rule: it needs the sibling values, so it is handled in the superRefine pass below.
      case FilterDefinitionRuleLabel.NOT_AFTER_END_OF:
        break;
    }
  }

  return schema;
}

/**
 * Appends a maximum limit constraint onto the schema. Works for string lengths or array item counts.
 * @param numValue - The maximum allowed limit number parsed from the backend rules.
 * @param schema - The current active Zod schema state structure.
 * @returns The modified Zod schema containing the maximum limit restriction condition.
 * * @example
 * // If schema is z.string() and numValue is 5
 * // Output: z.string().max(5, "Maximum 5 allowed")
 */
function applyMaxRule(
  numValue: number | null,
  schema: z.ZodType<unknown, unknown, z.core.$ZodTypeInternals<unknown, unknown>>
) {
  if (numValue !== null && (schema instanceof z.ZodString || schema instanceof z.ZodArray)) {
    schema = schema.max(numValue, `Maximum ${numValue} allowed`);
  }
  return schema;
}

/**
 * Appends a minimum limit constraint onto the schema. Works for string lengths or array item counts.
 * @param numValue - The minimum required threshold number parsed from the backend rules.
 * @param schema - The current active Zod schema state structure.
 * @returns The modified Zod schema containing the minimum limit restriction condition.
 * * @example
 * // If schema is z.array(...) and numValue is 2
 * // Output: schema.min(2, "Minimum 2 required")
 */
function applyMinRule(
  numValue: number | null,
  schema: z.ZodType<unknown, unknown, z.core.$ZodTypeInternals<unknown, unknown>>
) {
  if (numValue === null) {
    return schema;
  }
  if (schema instanceof z.ZodString || schema instanceof z.ZodArray || schema instanceof z.ZodNumber) {
    schema = schema.min(numValue, `Minimum ${numValue} required`);
  }
  return schema;
}

/**
 * Enforces a mandatory choice/input value selection rule onto the field.
 * @param schema - The current active Zod schema state structure.
 * @param field - The field definition, used to resolve the wording of the message.
 * @returns The modified Zod schema configured to reject empty entries.
 * * @example
 * // If schema is z.string() -> returns schema.min(1, 'filters.form.error.required')
 * // If schema is z.zodArray() -> returns schema.nonempty('filters.form.error.selectionRequired')
 */
function applyRequiredRule(
  schema: z.ZodType<unknown, unknown, z.core.$ZodTypeInternals<unknown, unknown>>,
  field: FieldDefinitionType
) {
  if (schema instanceof z.ZodArray) {
    schema = schema.nonempty(SELECTION_REQUIRED_MESSAGE);
  } else if (schema instanceof z.ZodString) {
    schema = schema.min(1, resolveRequiredMessage(field));
  }
  return schema;
}

/**
 * Rejects a selected value disabled by its restrictions, given the other values of the form.
 * DynamicForm already drops such values, this check guards against a forbidden combination coming from
 * a saved filter (e.g. like for like with e-commerce for ESP / BEL).
 */
function refineValueRestrictions(field: FieldDefinitionType, record: Record<string, unknown>, ctx: z.RefinementCtx) {
  const value = record[field.fieldName];
  const selectedValues = Array.isArray(value) ? value : [value];

  for (const option of field.options ?? []) {
    if (!selectedValues.includes(option.value)) {
      continue;
    }

    const { disabled, disabledReasonTranslationKey } = resolveFilterValueRestriction(option.restrictions, record);

    if (disabled) {
      ctx.addIssue({
        code: 'custom',
        path: [field.fieldName],
        message: disabledReasonTranslationKey
          ? `${RESTRICTED_VALUE_MESSAGE}.${disabledReasonTranslationKey}`
          : RESTRICTED_VALUE_MESSAGE
      });
    }
  }
}

/**
 * Validates cross-field conditional dependencies inside a Zod superRefine check.
 *
 * The activation rule mirrors `shouldShowField` in DynamicForm.vue: a field that is not rendered must not be
 * validated either, otherwise the form is blocked by an error the user cannot see.
 */
function refineFieldDependency(
  field: FieldDefinitionType,
  fields: FieldDefinitionType[],
  record: Record<string, unknown>,
  strictValidatorMap: Record<string, z.ZodTypeAny>,
  ctx: z.RefinementCtx
): void {
  const parentField = fields.find(({ fieldName }) => fieldName === field.dependency);

  // The parent filter is not part of the form (the user is not allowed to see it), so neither is this one
  if (!parentField) {
    return;
  }

  const parentValue = record[parentField.fieldName];
  const fieldValue = record[field.fieldName];

  const isDependencyActive =
    field.dependencyValue == null ? Boolean(parentValue) : parentValue === field.dependencyValue;

  if (!isDependencyActive) {
    return;
  }

  const strictValidator = strictValidatorMap[field.fieldName];
  const validationResult = strictValidator?.safeParse(fieldValue);

  if (validationResult && !validationResult.success) {
    validationResult.error.issues.forEach((issue) => {
      ctx.addIssue({
        ...issue,
        path: [field.fieldName, ...issue.path]
      });
    });
  }
}

/**
 * Returns the field a NOT_AFTER_END_OF rule points at, or undefined when the field carries no
 * such rule.
 *
 * The rule is declared in database as `{ label: 'NOT_AFTER_END_OF', value: '<other field name>' }`.
 */
function getDateBoundaryReferenceField(field: FieldDefinitionType): string | undefined {
  return field.validationRules?.find((rule) => rule.label === FilterDefinitionRuleLabel.NOT_AFTER_END_OF)?.value;
}

/**
 * Reads the granularity currently selected in the form, or undefined when it is not set yet.
 */
function resolveGranularity(values: Record<string, unknown>): PeriodGranularityType | undefined {
  const granularity = values[GRANULARITY_FIELD];

  return isPeriodGranularity(granularity) ? granularity : undefined;
}

/**
 * Resolves the latest date a field may start at, given the current value of the field its
 * NOT_AFTER_END_OF rule references. Returns undefined when the boundary cannot be resolved yet.
 */
export function resolveDateBoundary(
  field: FieldDefinitionType,
  values: Record<string, unknown>,
  getReferentialInterval?: SchemaContextType['getReferentialInterval']
): Dayjs | undefined {
  const referenceFieldName = getDateBoundaryReferenceField(field);
  const referenceValue = referenceFieldName ? values[referenceFieldName] : undefined;

  if (typeof referenceValue !== 'string') {
    return undefined;
  }

  const granularity = resolveGranularity(values);
  const referentialInterval = granularity ? (getReferentialInterval?.(granularity) ?? null) : null;

  return resolvePeriodRange(referenceValue, granularity, referentialInterval)?.end;
}

/**
 * Enforces the NOT_AFTER_END_OF rule: a date range may not start after the end of the range it
 * is compared against. Used by the custom comparison period, whose start must stay within the
 * analysis period.
 */
function refineDateBoundary(
  field: FieldDefinitionType,
  record: Record<string, unknown>,
  ctx: z.RefinementCtx,
  getReferentialInterval: SchemaContextType['getReferentialInterval']
): void {
  const boundary = resolveDateBoundary(field, record, getReferentialInterval);
  const fieldRange = resolvePeriodRange(record[field.fieldName]);

  // An unresolvable range is already reported by the value format validator
  if (!boundary || !fieldRange) {
    return;
  }

  if (fieldRange.start.isAfter(boundary)) {
    ctx.addIssue({
      code: 'custom',
      path: [field.fieldName],
      message: 'filters.form.error.comparisonStartAfterReferenceEnd'
    });
  }
}

/**
 * A preset the UI never offers for the selected granularity (previous period in daily mode) can only
 * come from a stale saved value: the user is asked to pick the period again.
 */
function refinePeriodPresetAvailability(record: Record<string, unknown>, ctx: z.RefinementCtx): void {
  const value = parsePeriodValue(record[PERIOD_FIELD]);
  const granularity = resolveGranularity(record);

  if (value?.kind !== 'preset' || !granularity || isPresetAvailable(value.option, granularity)) {
    return;
  }

  ctx.addIssue({
    code: 'custom',
    path: [PERIOD_FIELD],
    message: REQUIRED_MESSAGE_BY_FIELD[PERIOD_FIELD] ?? REQUIRED_MESSAGE
  });
}

/**
 * The primary orchestrator function. Takes a list of form field configurations
 * and loops through them recursively to build a complete, production-ready Zod Form Schema object.
 *
 * This orchestrator handles both static validations and complex, cross-field conditional layouts.
 * Fields marked with a `dependency` identifier are dynamically isolated and verified via a runtime
 * structural refinement pass.
 * If a field's active parent context matches its designated `dependencyValue`, or doesn't have a `dependencyValue`,
 * the strict internal validation matrix rule configuration is fully evaluated. If the context is inactive,
 * the field gracefully bypasses checks as an optional parameter.
 * @param fields - An array containing all dynamic field metadata rules configuration definitions.
 * @returns A complete, refined Zod Object validator schema map matching your dynamic data layout.
 * @example
 * Input: [
 *   { fieldName: 'history', element: 'RADIO', validationRules: [{ label: 'REQUIRED', value: true }] },
 *   { fieldName: 'custom_history', element: 'DATE_RANGE', dependency: 'history', dependencyValue: 'perso', validationRules: [{ label: 'REQUIRED', value: true }] }
 * ]
 * Output: A schema enforcing that 'custom_history' must match the period value contract AND be present ONLY IF 'history' equals 'perso'.
 */
export function createSchema(
  fields: FieldDefinitionType[],
  { getReferentialInterval }: SchemaContextType = {}
): z.ZodTypeAny {
  const shape: Record<string, z.ZodTypeAny> = {};
  const strictValidatorMap: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    // 1. Resolve the core element (e.g., z.string())
    const baseValidator = baseSchema(field);

    // 2. Apply validation rules to the raw core validator FIRST
    const ruleAppliedValidator = applyRules(baseValidator, field);

    // 3. Store this fully-ruled strict validator in our map for conditional refinement
    strictValidatorMap[field.fieldName] = ruleAppliedValidator;

    // 4. Build the parent shape validator.
    // If it has a dependency, we make the base element optional & nullable in the
    // root schema so that inactive payloads can pass base validation.
    let shapeValidator = ruleAppliedValidator;
    if (field.dependency) {
      shapeValidator = baseValidator.optional().nullable();
    }

    shape[field.fieldName] = shapeValidator;
  }

  let objectSchema: z.ZodTypeAny = z.object(shape);

  const fieldsWithDependencies = fields.filter((f) => f.dependency);
  const fieldsWithDateBoundary = fields.filter((f) => getDateBoundaryReferenceField(f));
  const hasPeriodField = fields.some((f) => f.fieldName === PERIOD_FIELD);
  const fieldsWithRestrictions = fields.filter((f) => f.options?.some((option) => option.restrictions?.length));

  if (
    fieldsWithDependencies.length > 0 ||
    fieldsWithDateBoundary.length > 0 ||
    hasPeriodField ||
    fieldsWithRestrictions.length > 0
  ) {
    objectSchema = objectSchema.superRefine((data, ctx) => {
      const record = data as Record<string, unknown>;

      for (const field of fieldsWithDependencies) {
        refineFieldDependency(field, fields, record, strictValidatorMap, ctx);
      }

      for (const field of fieldsWithDateBoundary) {
        refineDateBoundary(field, record, ctx, getReferentialInterval);
      }

      if (hasPeriodField) {
        refinePeriodPresetAvailability(record, ctx);
      }

      for (const field of fieldsWithRestrictions) {
        refineValueRestrictions(field, record, ctx);
      }
    });
  }

  return objectSchema;
}
